import { Link } from 'react-router'
import { type Route } from './+types/about.ts'

export const meta: Route.MetaFunction = () => [{ title: 'About | Podly' }]

const cardStyles = `border-foreground bg-base-100/40 text-foreground flex-row space-y-6 rounded-2xl border p-8
	text-left text-lg leading-relaxed hover:scale-[1.01] transition duration-300 animate-pulse-shadow shadow-lg md:p-12`

export default function AboutRoute() {
	return (
		<main className="text-base-content font-poppins from-background flex min-h-screen flex-col bg-gradient-to-b via-indigo-500/5 to-purple-500/10">
			{/* Hero Section */}
			<div className="mx-4">
				<section className="mx-auto py-24">
					<div className="mx-auto max-w-4xl">
						<h1 className="text-foreground mb-6 text-center text-5xl font-extrabold tracking-tight md:text-6xl">
							About <span className="text-indigo-400">Podly</span>
						</h1>
						<div className="mx-auto flex max-w-4xl justify-center">
							<div className={cardStyles}>
								<h2 className="mb-6 text-3xl font-bold md:text-4xl">
									Hi, I'm Sam
								</h2>
								<div className="flex flex-col justify-center gap-10 md:flex-row">
									<img
										src="/img/sam-astro.png"
										alt="Sam Astro"
										className="max-w-full rounded-2xl object-cover sm:max-w-64"
									/>
									<div className="flex flex-col justify-center gap-10">
										<p>
											I was a Technical Director at a church for two years,
											editing hundreds of sermons. I never had the budget for
											iZotope RX, so I made do with the plugins I had — but as
											an audio engineer, I was never satisfied with the results.
										</p>
										<p>
											As I grew as a software engineer I realized I could build
											a{' '}
											<strong>
												<em>better solution.</em>
											</strong>
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Story Section */}
				<section className="mx-auto py-16">
					<div className="mx-auto max-w-4xl">
						<h2 className="text-foreground mb-6 text-center text-3xl font-bold md:text-4xl">
							So I Built Podly
						</h2>
						<div className="text-foreground flex flex-col justify-center space-y-6 text-left text-lg leading-relaxed md:p-12">
							<p>
								Editing sermons taught me that noise reduction, in particular,
								could be faster and easier. Many episodes just need trimming,
								noise reduction, and audio enhancement to sound comfortable to
								listen to. I love working in my DAW with complex tools, but I
								designed Podly with a simple interface that gets the job done
								quickly.
							</p>
							<p>That's what Podly is for.</p>
							<img
								src="/img/podly-editor.png"
								alt="Podly Story"
								className="rounded-2xl object-cover sm:mx-12"
							/>
							<p>
								As an audio engineer by training, I know some situations call
								for deeper, more advanced tools. But for most, Podly speeds up
								your workflow and delivers professional results.
							</p>
							<p>
								If your church wants to turn sermons into professional podcasts,
								Podly is for you.
							</p>
						</div>
					</div>
				</section>

				{/* Values Section */}
				<section className="mx-auto py-16">
					<div className="mx-auto max-w-6xl">
						<h2 className="text-foreground mb-12 text-center text-3xl font-bold md:text-4xl">
							What We Stand For
						</h2>
						<div className="grid gap-8 md:grid-cols-3">
							{values.map((value) => (
								<div
									key={value.title}
									className="border-foreground bg-base-100/40 rounded-2xl border p-6 transition hover:border-indigo-500"
								>
									<div className="mb-4 text-4xl text-indigo-400">
										{value.icon}
									</div>
									<h3 className="text-foreground mb-2 text-xl font-bold">
										{value.title}
									</h3>
									<p className="text-foreground">{value.description}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* Technology Section */}
				<section className="py-16">
					<div className="mx-auto max-w-4xl">
						<div className="border-foreground bg-base-100/40 rounded-2xl border p-8 md:p-12">
							<h2 className="text-foreground mb-6 text-3xl font-bold md:text-4xl">
								Built for Modern Creators
							</h2>
							<p className="text-foreground mb-4 text-lg leading-relaxed">
								Podly is built on a modern, React Framework using{' '}
								<a
									href="https://www.epicweb.dev/epic-stack"
									target="_blank"
									rel="noopener noreferrer"
								>
									The Epic Stack
								</a>
								, Prisma, and Tigris Cloud Storage. This means fast performance,
								reliable storage, and a seamless experience whether you're on
								desktop or mobile.
							</p>
							<p className="text-foreground text-lg leading-relaxed">
								We use a combination of AI and traditional audio engineering
								techniques to produce the best possible results. Our AI-powered
								audio enhancement uses{' '}
								<a
									href="https://github.com/Rikorose/DeepFilterNet"
									target="_blank"
									rel="noopener noreferrer"
								>
									DeepFilterNet2
								</a>{' '}
								to automatically clean up background noise on dialogue, and
								enhance speech clarity. We also are developing a new AI model to
								enhance the audio of the broadcast including music and other
								audio.
							</p>
						</div>
					</div>
				</section>

				{/* Call to Action */}
				<section className="text-foreground py-24 text-center">
					<h2 className="mb-6 text-3xl font-bold md:text-4xl">
						Ready to Get Started?
					</h2>
					<p className="text-foreground/70 mx-auto mb-8 max-w-xl">
						Join creators using Podly to turn raw audio into professional
						podcasts.
					</p>
					<div className="flex flex-wrap justify-center gap-4">
						<Link
							to="/editor"
							className="rounded-full bg-indigo-500 px-8 py-3 font-semibold text-white transition hover:bg-indigo-600"
						>
							Try Podly Free
						</Link>
						<Link
							to="/signup"
							className="border-foreground/30 text-foreground/90 hover:text-foreground rounded-full border px-8 py-3 font-semibold transition hover:border-indigo-400"
						>
							Create Account
						</Link>
					</div>
				</section>
			</div>
		</main>
	)
}

const values = [
	{
		title: 'Simplicity',
		description:
			'We believe powerful tools should be easy to use. No complex interfaces, no steep learning curves—just straightforward podcast creation.',
		icon: '🎯',
	},
	{
		title: 'Quality',
		description:
			'Every feature is designed to help you produce professional-quality content. From AI enhancement to seamless exports, quality is built in.',
		icon: '✨',
	},
	{
		title: 'Accessibility',
		description: `Podcast creation shouldn't be limited by budget or technical skills. We're making professional tools accessible to everyone.`,
		icon: '🌍',
	},
]
